"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VerificationStatus = void 0;
/*!
 * Copyright (c) 2018, imqueue.com <support@imqueue.com>
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
 * REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
 * AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
 * INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
 * LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
 * OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
 * PERFORMANCE OF THIS SOFTWARE.
 */
const ioredis_1 = require("ioredis");
const request_ip_1 = require("request-ip");
const net_1 = require("@imqueue/net");
var VerificationStatus;
(function (VerificationStatus) {
    VerificationStatus[VerificationStatus["SAFE"] = 0] = "SAFE";
    VerificationStatus[VerificationStatus["LIMITED"] = 1] = "LIMITED";
    VerificationStatus[VerificationStatus["BANNED"] = 2] = "BANNED";
})(VerificationStatus || (exports.VerificationStatus = VerificationStatus = {}));
const HTTP_TEXT = {
    418: 'I\'m a teapot',
    429: 'Too Many Requests',
};
// noinspection JSUnusedGlobalSymbols
/**
 * HttpProtect
 * Provides middleware for express-like web server to protect from DDoS attacks.
 * It uses Redis as a storage for requests counters and block list.
 * It uses @imqueue/net Networks class to deal with block list at runtime.
 */
class HttpProtect {
    options;
    redis;
    prefix;
    ttl;
    maxRequests;
    banLimit;
    blockListKey;
    constructor(options) {
        this.options = options;
        this.redis = options?.redis || this.connect(options?.redisOptions);
        this.prefix = this.options?.redisPrefix || 'imq:http-protect';
        this.maxRequests = this.options?.maxRequests ||
            +(process.env.HTTP_PROTECT_MAX_REQUESTS || 200);
        this.ttl = this.options?.ttl || +(process.env.HTTP_PROTECT_TTL || 10);
        this.banLimit = this.options?.banLimit ||
            +(process.env.HTTP_PROTECT_BAN_LIMIT || 1000);
        this.blockListKey = `${this.prefix}:block-list`;
    }
    connect(options) {
        this.redis = new ioredis_1.default(options || {
            host: 'localhost',
            port: 6379,
        });
        return this.redis;
    }
    async verify(req) {
        if (!this.redis) {
            throw new Error('Redis connection is not established!');
        }
        const ip = (0, request_ip_1.getClientIp)(req) || '';
        const key = `${this.prefix}:${ip}`;
        if (await this.redis?.sismember(this.blockListKey, ip)) {
            return {
                status: VerificationStatus.BANNED,
                httpCode: 418,
            };
        }
        let requests = 1;
        if (!await this.redis?.setnx(key, 1)) {
            requests = await this.redis?.incr(key) || 1;
        }
        // noinspection TypeScriptValidateTypes
        await this.redis?.expire(key, this.ttl);
        if (requests > this.maxRequests) {
            if (requests > this.banLimit) {
                // noinspection TypeScriptValidateTypes
                await this.redis?.sadd(this.blockListKey, ip);
                return {
                    status: VerificationStatus.BANNED,
                    httpCode: 418,
                };
            }
            return {
                status: VerificationStatus.LIMITED,
                httpCode: 429,
            };
        }
        return {
            status: VerificationStatus.SAFE,
            httpCode: 200,
        };
    }
    async bannedNetworks() {
        // noinspection TypeScriptValidateTypes
        const ips = await this.redis?.smembers(this.blockListKey) || [];
        return new net_1.Networks(ips.map(ip => `${ip}/32`));
    }
    // noinspection JSUnusedGlobalSymbols
    async isBanned(ip) {
        const networks = await this.bannedNetworks();
        return networks.includes(ip);
    }
    async isLimited(ip) {
        const key = `${this.prefix}:${ip}`;
        const requests = +(await this.redis?.get(key) || 0);
        return requests > this.maxRequests;
    }
    middleware() {
        return async (req, res, next) => {
            const { status, httpCode } = await this.verify(req);
            if (status === VerificationStatus.SAFE) {
                return next();
            }
            res.status(httpCode);
            res.end();
        };
    }
    textMiddleware() {
        return async (req, res, next) => {
            const { status, httpCode } = await this.verify(req);
            if (status === VerificationStatus.SAFE) {
                return next();
            }
            res.status(httpCode);
            if (typeof res.header === 'function') {
                res.header('Content-Type', 'text/plain');
            }
            else if (typeof res.setHeader === 'function') {
                res.setHeader('Content-Type', 'text/plain');
            }
            res.send(`${httpCode} ${HTTP_TEXT[httpCode]}`);
            res.end();
        };
    }
    jsonMiddleware() {
        return async (req, res, next) => {
            const { status, httpCode } = await this.verify(req);
            if (status === VerificationStatus.SAFE) {
                return next();
            }
            res.status(httpCode);
            if (typeof res.header === 'function') {
                res.header('Content-Type', 'application/json');
            }
            else if (typeof res.setHeader === 'function') {
                res.setHeader('Content-Type', 'application/json');
            }
            res.send(JSON.stringify({
                error: {
                    type: 'HTTP',
                    code: httpCode,
                    message: HTTP_TEXT[httpCode],
                },
            }));
            res.end();
        };
    }
    destroy() {
        this.redis?.disconnect();
        this.redis = undefined;
    }
}
exports.default = HttpProtect;
//# sourceMappingURL=index.js.map